import React from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  Checkbox,
  TextField,
  Button,
  Tooltip,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const useStyles = makeStyles(theme => ({
  button: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important'
  },
  selected: {
    color: theme.palette.primary.main,
    margin: '0px 8px'
  },
  checkbox: {
    paddingLeft: '0px'
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%'
  },
  label: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: 'auto'
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  radioLabel: {
    margin: '0 4px',
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      color: 'rgba(0,0,0,.65)'
    },
    '& .MuiRadio-root': {
      paddingLeft: '4px',
      paddingRight: '4px'
    }
  }
}));

export default function AutoCompleteCheckbox(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { filter, action, showStatusFilter, statusFilter, onStatusChange } = props;
  const { options, onChange, title } = filter;
  const [isSelected, setIsSelected] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [internalStatusFilter, setInternalStatusFilter] = React.useState('all');
  const currentStatusFilter = statusFilter !== undefined ? statusFilter : internalStatusFilter;

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    setIsSelected(options.filter(i => i.checked).length > 0);
  }, [options]);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleStatusChange = (event) => {
    const newValue = event.target.value;
    if (onStatusChange) {
      onStatusChange(newValue);
    } else {
      setInternalStatusFilter(newValue);
    }
  };

  const handleChange = event => {
    let newOptions = options.map(option => {
      if (option.value === event.value) {
        option.checked = !option.checked;
      }
      return option;
    });
    onChange(
      newOptions.filter(option => option.checked).map(option => option.value)
    );
    dispatch(action);
  };

  return (
    <>
      <Button
        aria-label={`Filter ${title}`}
        ref={anchorRef}
        className={isSelected ? classes.selected : classes.button}
        onClick={handleToggle}
        endIcon={<ExpandMoreIcon />}>
        {title}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition>
        {({ TransitionProps, placement }) => (
          <Paper>
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom'
              }}>
              <ClickAwayListener onClickAway={handleClose}>
                <div id="split-button-menu">
                  <Autocomplete
                    multiple
                    id="checkboxes-tags"
                    disableCloseOnSelect
                    options={options}
                    open={true}
                    role={undefined}
                    renderTags={() => {}}
                    size="small"
                    getOptionLabel={option =>
                      option.displayName ? option.displayName : option.value
                    }
                    PaperComponent={({ children }) => (
                      <Paper>
                        {showStatusFilter && (
                          <FormControl component="fieldset" style={{ width: '100%' }}>
                            <RadioGroup
                              className={classes.radioGroup}
                              value={currentStatusFilter}
                              onChange={handleStatusChange}
                              row
                              style={{ justifyContent: 'flex-start', flexWrap: 'nowrap' }}
                            >
                              <FormControlLabel
                                value="all"
                                control={<Radio color="primary" size="small" />}
                                label="All"
                                className={classes.radioLabel}
                              />
                              <FormControlLabel
                                value="active"
                                control={<Radio color="primary" size="small" />}
                                label="Active"
                                className={classes.radioLabel}
                              />
                              <FormControlLabel
                                value="archived"
                                control={<Radio color="primary" size="small" />}
                                label="Archived"
                                className={classes.radioLabel}
                              />
                            </RadioGroup>
                          </FormControl>
                        )}
                        {children}
                      </Paper>
                    )}
                    renderOption={option => (
                      <Tooltip
                        title={
                          option.displayName ? option.displayName : option.value
                        }>
                        <div
                          onClick={() => handleChange(option)}
                          className={classes.listItem}>
                          <Checkbox
                            color="primary"
                            style={{ marginRight: 8 }}
                            checked={option.checked}
                          />
                          <span className={classes.label}>
                            {option.displayName
                              ? option.displayName
                              : option.value}
                          </span>
                        </div>
                      </Tooltip>
                    )}
                    style={{ width: showStatusFilter ? 230 : 200 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: ''
                        }}
                        variant="outlined"
                        label={`Search ${title}`}
                      />
                    )}
                  />
                </div>
              </ClickAwayListener>
            </Grow>
          </Paper>
        )}
      </Popper>
    </>
  );
}
